import { makeStyles } from '@mui/styles';
import Checkbox from 'apollo-react/components/Checkbox';
import Table, { createStringSearchFilter } from 'apollo-react/components/Table';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import ActionsCell from './controls/ActionsCell';
import { CheckboxCell } from './controls/CheckboxCell';
import MessageCell from './controls/MessageCell';
import SelectCell from './controls/SelectCell';
import StatusCell from './controls/StatusCell';
import TextFieldFilter from '../../../../components/TableFilters/TextInputFilter';
import { useTranslation } from '../../../../custom-hooks/useTranslation';
import { bulkChangeImportStatuses, changeImportStatus } from '../../../actions/import.actions';
import {
  ACTION_ACCESSOR,
  ACTION_FOR_DUPLICATE_ITEM_ACCESSOR,
  ACTION_FOR_DUPLICATE_ITEM_HEADER,
  APPLICATION_COMPONENT_NAME_ACCESSOR,
  APPLICATION_COMPONENT_NAME_HEADER,
  CHECKED_ACCESSOR,
  COMMENT_ACCESSOR,
  COMMENT_HEADER_IMPORT,
  ID_ACCESSOR,
  ID_HEADER,
  LAST_MODIFIED_BY_ACCESSOR,
  LAST_MODIFIED_BY_HEADER,
  LAST_MODIFIED_DATE_ACCESSOR,
  LAST_MODIFIED_DATE_HEADER,
  MESSAGE_ACCESSOR,
  MESSAGE_HEADER,
  STATUS_ACCESSOR,
  STATUS_HEADER,
  TYPE_ACCESSOR,
  TYPE_HEADER,
} from '../../../constants/common.constants';
import { IMPORT_STEPS } from '../../../constants/import.constants';

const useStyles = makeStyles({
  statusTable: {
    border: 'solid 1px #e9e9e9',
    boxShadow: '0 4px 16px 0 rgb(0 0 0 / 4%)',
    marginBottom: 64,
  },
});

const columns = [
  {
    header: ID_HEADER,
    accessor: ID_ACCESSOR,
    hidden: true,
  },
  {
    header: ACTION_FOR_DUPLICATE_ITEM_HEADER,
    accessor: ACTION_FOR_DUPLICATE_ITEM_ACCESSOR,
    filterFunction: createStringSearchFilter(ACTION_FOR_DUPLICATE_ITEM_ACCESSOR),
    filterComponent: TextFieldFilter,
    customCell: SelectCell,
  },
  {
    header: APPLICATION_COMPONENT_NAME_HEADER,
    accessor: APPLICATION_COMPONENT_NAME_ACCESSOR,
    filterFunction: createStringSearchFilter(APPLICATION_COMPONENT_NAME_ACCESSOR),
    filterComponent: TextFieldFilter,
  },
  {
    header: TYPE_HEADER,
    accessor: TYPE_ACCESSOR,
    filterFunction: createStringSearchFilter(TYPE_ACCESSOR),
    filterComponent: TextFieldFilter,
  },
  {
    header: LAST_MODIFIED_DATE_HEADER,
    accessor: LAST_MODIFIED_DATE_ACCESSOR,
    filterFunction: createStringSearchFilter(LAST_MODIFIED_DATE_ACCESSOR),
    filterComponent: TextFieldFilter,
  },
  {
    header: LAST_MODIFIED_BY_HEADER,
    accessor: LAST_MODIFIED_BY_ACCESSOR,
    filterFunction: createStringSearchFilter(LAST_MODIFIED_BY_ACCESSOR),
    filterComponent: TextFieldFilter,
  },
  {
    header: STATUS_HEADER,
    accessor: STATUS_ACCESSOR,
    filterFunction: createStringSearchFilter(STATUS_ACCESSOR),
    filterComponent: TextFieldFilter,
    customCell: StatusCell,
  },
  {
    header: MESSAGE_HEADER,
    accessor: MESSAGE_ACCESSOR,
    filterFunction: createStringSearchFilter(MESSAGE_ACCESSOR),
    filterComponent: TextFieldFilter,
    customCell: MessageCell,
  },
  {
    header: COMMENT_HEADER_IMPORT,
    accessor: COMMENT_ACCESSOR,
    filterFunction: createStringSearchFilter(COMMENT_ACCESSOR),
    filterComponent: TextFieldFilter,
  },
];

export const StatusTable = ({ rows, importKey, importStep, zipFieldId, isLoading }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const [selectedRows, setSelectedRows] = useState(new Set());
  const [filteredRows, setFilteredRows] = useState(new Set());

  const isUploadStep = importStep === IMPORT_STEPS.UPLOAD_FILE;
  const isCompletedStep = importStep === IMPORT_STEPS.UPLOAD_COMPLETED;

  const selectedIntersection = new Set([...selectedRows].filter((x) => filteredRows.has(x)));
  const isAllSelected = selectedIntersection.size === filteredRows.size;
  const isIndeterminate =
    selectedIntersection.size > 0 && selectedIntersection.size < filteredRows.size;

  useEffect(() => {
    setSelectedRows(new Set(rows.filter((r) => r.Checked).map((row) => row.id)));
    !filteredRows.size && setFilteredRows(new Set(rows.map((row) => row.id)));
  }, [filteredRows.size, rows]);

  const handleEditRow = (params) => {
    const { id, key, value } = params;
    dispatch(changeImportStatus(importKey, id, key, value));
  };

  const onCheckboxHeaderChange = () => {
    dispatch(
      bulkChangeImportStatuses({
        importKey,
        key: CHECKED_ACCESSOR,
        value: !isAllSelected,
        ids: filteredRows,
      })
    );
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }

    dispatch(changeImportStatus(importKey, id, CHECKED_ACCESSOR, newSelected.has(id)));

    setSelectedRows(newSelected);
  };

  const onTableChange = (values) => {
    const { rows: filteredRows } = values;
    setFilteredRows(new Set(filteredRows.map((row) => row.id)));
  };

  const checkboxColumn = {
    header: (
      <Checkbox
        indeterminate={isIndeterminate}
        checked={isAllSelected}
        onChange={onCheckboxHeaderChange}
      />
    ),
    accessor: '',
    customCell: CheckboxCell,
  };

  const actionsColumn = {
    accessor: ACTION_ACCESSOR,
    customCell: ActionsCell,
    align: 'right',
  };

  return (
    <div className={classes.statusTable}>
      <Table
        rowId="id"
        showFilterIcon
        columns={
          isUploadStep
            ? [checkboxColumn, ...columns]
            : isCompletedStep
            ? [...columns, actionsColumn]
            : [...columns]
        }
        rows={rows.map((row) => ({
          ...row,
          selectedRows,
          importStep,
          importKey,
          zipFieldId,
          isLoading,
          handleEditRow,
          handleSelectRow,
        }))}
        onChange={onTableChange}
        defaultRowsPerPage={25}
        rowsPerPageOptions={[15, 25, 50, 200, translateString('All')]}
        emptyProps={{
          text: translateString('No data to display'),
        }}
        tablePaginationProps={{
          labelDisplayedRows: ({ from, to, count }) =>
            `${from}-${to} ${translateString('of')} ${count}`,
          truncate: false,
          labelRowsPerPage: translateString('Rows'),
          pageText: translateString('Page'),
          ofText: translateString('of'),
        }}
      />
    </div>
  );
};
